// components/portal/GenericEntitySelector.tsx
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useKernelStore } from '@/kernel/kernelStores';
import { useKernel } from '@/kernel/Kernel';
import { InventoryEntityType, LeasingOrganisationDTO, LeasingUserDTO } from '#types/types';
import { EntityView } from '../entities/EntityView';
import { Search, X, Building, Globe, Ruler, Check } from '@/components/ui/lucide-memo';
import SimpleLoader from '../ui/SimpleLoader';
import clsx from 'clsx';
import UserAvatar from '../UserAvatar';

interface GenericEntitySelectorProps {
  type: InventoryEntityType;
  selectedIds: string[];
  onSelect: (id: string) => void;
  isMultiSelect?: boolean;
  onConfirmSelection?: () => Promise<void> | void;
  filters?: Record<string, any>;
  disabled?: boolean;
  className?: string;
  renderTrigger: (selectedItems: any[], onClick: () => void) => React.ReactNode;
  variant?: 'row' | 'card';
  title?: string;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  onClearSelection?: () => void;
  multiSelectFooterExtra?: React.ReactNode;
}

const formatTimeAgo = (dateString: string | null) => {
  if (!dateString) return 'Never active';
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

const getUnitVisual = (item: any): { src: string; kind: 'thumbnail' | 'logo' } | null => {
  if (item.thumbnailFileId) return { src: `/api/inventory/assets/${item.thumbnailFileId}`, kind: 'thumbnail' };
  if (item.thumbnailFile?.filePath) return { src: `/api/inventory/assets/${item.thumbnailFile.filePath}`, kind: 'thumbnail' };
  if (item.brandLogoUrl) return { src: item.brandLogoUrl, kind: 'logo' };
  return null;
};

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

const handleUnitImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.style.display = 'none';
};

/**
 * ChatGPT's picker row (owner, 2026-09-28: 'chatgptize this menu'): a round visual, the name and a
 * quiet second line, a check on the current choice. Members and organisations are a short list to
 * pick one from, not a gallery to browse.
 */
const PickerRow = React.memo(function PickerRow({ id, name, detail, visual, isSelected, onSelect }: {
  id: string;
  name: string;
  detail?: string;
  visual: React.ReactNode;
  isSelected: boolean;
  onSelect: (id: string) => void;
}) {
  const handleClick = useCallback(() => onSelect(id), [onSelect, id]);
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={isSelected}
      className="flex w-full items-center gap-3 rounded-[12px] px-3 py-2 text-left transition-colors hover:bg-white/[0.06]"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#2A2A2A] text-[#ECECEC]">{visual}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }}>{name}</span>
        {detail ? <span className="block truncate text-[#8E8E8E]" style={{ fontSize: 12, lineHeight: '16px' }}>{detail}</span> : null}
      </span>
      {isSelected ? <Check size={18} className="shrink-0 text-[#ECECEC]" /> : null}
    </button>
  );
});

interface UnitCardProps {
  unit: any;
  isSelected: boolean;
  hideProperty: boolean;
  onSelect: (id: string) => void;
}

const UnitCard = React.memo(function UnitCard({ unit, isSelected, hideProperty, onSelect }: UnitCardProps) {
  const handleClick = useCallback(() => onSelect(unit.id), [onSelect, unit.id]);
  const visual = getUnitVisual(unit);
  const isAvailable = unit.status === 'AVAILABLE' || unit.status === 'EXPIRING_SOON';
  const statusLabel = unit.status ? String(unit.status).replace(/_/g, ' ') : 'UNKNOWN';
  const areaLabel = unit.areaSqm
    ? `${Math.round(unit.areaSqm)} m²`
    : unit.areaSqft
      ? `${Math.round(unit.areaSqft * 0.0929)} m²`
      : '-';

  return (
    <button
      onClick={handleClick}
      className={clsx(
        'flex flex-col text-left group overflow-hidden shadow-xl rounded-2xl border transition-all',
        isSelected
          ? 'border-emerald-500/50 bg-emerald-500/10 ring-1 ring-emerald-500/50'
          : 'bg-neutral-800/20 border-neutral-700/50 hover:bg-neutral-800/40 hover:border-white/20'
      )}
    >
      <div className="p-3 px-4 bg-neutral-950/40 border-b border-neutral-800 flex flex-col justify-center gap-1 min-h-[58px]">
        {!hideProperty && (
          <p className="text-[9px] font-medium text-neutral-500 truncate">
            {unit.propertyName || 'Unknown Property'}
          </p>
        )}
        <p className="text-[12px] font-bold text-neutral-200 line-clamp-1 group-hover:text-white transition-colors">
          Unit {unit.unitIdentifier || unit.id}
        </p>
      </div>

      <div className="w-full aspect-[16/10] relative bg-neutral-800/30 flex items-center justify-center overflow-hidden">
        {visual ? (
          <img
            src={visual.src}
            alt={unit.unitIdentifier || 'Unit'}
            className={clsx(
              'w-full h-full',
              visual.kind === 'logo'
                ? 'object-contain p-3 bg-white'
                : 'object-cover'
            )}
            onError={handleUnitImageError}
          />
        ) : (
          <div className="px-4 text-center">
            <p className="line-clamp-3 text-[18px] font-semibold leading-tight text-neutral-200">
              {typeof unit.tenantName === 'string' && unit.tenantName.trim().length > 0
                ? unit.tenantName
                : `Unit ${unit.unitIdentifier || unit.id}`}
            </p>
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-neutral-800 bg-neutral-950/20 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className={clsx(
            'text-[10px] px-2 py-0.5 rounded-md font-semibold',
            isAvailable ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-white/60'
          )}>
            {statusLabel}
          </span>
          <span className="text-[10px] text-neutral-300 flex items-center gap-1">
            <Ruler size={10} className="opacity-70" />
            {areaLabel}
          </span>
        </div>
      </div>
    </button>
  );
});

interface GenericEntityCardProps {
  type: InventoryEntityType;
  item: any;
  variant: 'row' | 'card';
  isSelected: boolean;
  onSelect: (id: string) => void;
}

const GenericEntityCard = React.memo(function GenericEntityCard({ type, item, variant, isSelected, onSelect }: GenericEntityCardProps) {
  const handleClick = useCallback(() => onSelect(item.id), [onSelect, item.id]);
  return (
    <div
      onClick={handleClick}
      className={clsx(
        'rounded-2xl overflow-hidden transition-all cursor-pointer border shadow-lg',
        isSelected
          ? 'border-emerald-500/50 bg-emerald-500/10 ring-1 ring-emerald-500/50'
          : 'border-neutral-700/50 bg-neutral-800/20 hover:bg-neutral-800/40 hover:border-white/20'
      )}
    >
      <div className="pointer-events-none h-full">
        <EntityView type={type} entityId={item.id} variant={variant} />
      </div>
    </div>
  );
});

export const GenericEntitySelector = ({
  type,
  selectedIds,
  onSelect,
  isMultiSelect,
  onConfirmSelection,
  filters,
  disabled,
  className,
  renderTrigger,
  variant = 'card',
  title,
  isOpen,
  onOpenChange,
  onClearSelection,
  multiSelectFooterExtra
}: GenericEntitySelectorProps) => {
  const { kernel } = useKernel();
  const [internalOpen, setInternalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [localOrder, setLocalOrder] = useState<string[]>([]);
  const [localPagination, setLocalPagination] = useState<{ limit: number; offset: number; total: number; hasMore: boolean } | null>(null);
  const [localIsLoading, setLocalIsLoading] = useState(false);
  const [isConfirmingSelection, setIsConfirmingSelection] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const localFiltersRef = useRef<Record<string, any>>({});

  const modalOpen = isOpen ?? internalOpen;

  const setOpen = useCallback((next: boolean) => {
    if (isOpen === undefined) {
      setInternalOpen(next);
    }
    onOpenChange?.(next);
  }, [isOpen, onOpenChange]);

  const slice = useKernelStore(state => state.inventory[type]);
  const registry = slice?.registry || {};
  const propertyRegistry = useKernelStore(state => state.inventory.Property.registry);

  const filtersString = JSON.stringify(filters || {});

  useEffect(() => {
    if (!modalOpen || !kernel.current) return;

    const parsedFilters = JSON.parse(filtersString);
    const seededFilters = { ...parsedFilters, search: '' };
    localFiltersRef.current = seededFilters;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
    setLocalIsLoading(true);
    void kernel.current.queryInventory(type, { filters: seededFilters, offset: 0 }).then((result) => {
      setLocalOrder(result.ids);
      setLocalPagination(result.pagination as any);
    }).finally(() => {
      setLocalIsLoading(false);
    });
    setSearch('');
  }, [modalOpen, type, kernel, filtersString]);

  const handleSearch = useCallback((val: string) => {
    setSearch(val);
    const nextFilters = { ...(localFiltersRef.current || {}), search: val };
    localFiltersRef.current = nextFilters;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setLocalIsLoading(true);
      void kernel.current?.queryInventory(type, { filters: nextFilters, offset: 0 }).then((result) => {
        setLocalOrder(result.ids);
        setLocalPagination(result.pagination as any);
      }).finally(() => {
        setLocalIsLoading(false);
      });
    }, 300);
  }, [kernel, type]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearch(e.target.value);
  }, [handleSearch]);

  useEffect(() => () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
  }, []);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (!localIsLoading && localPagination?.hasMore) {
        const nextOffset = (localPagination.offset ?? 0) + (localPagination.limit ?? 100);
        setLocalIsLoading(true);
        void kernel.current?.queryInventory(type, {
          filters: localFiltersRef.current,
          limit: localPagination.limit ?? 100,
          offset: nextOffset,
        }).then((result) => {
          setLocalOrder((prev) => {
            const existing = new Set(prev);
            return [...prev, ...result.ids.filter((id) => !existing.has(id))];
          });
          setLocalPagination(result.pagination as any);
        }).finally(() => {
          setLocalIsLoading(false);
        });
      }
    }
  }, [localIsLoading, localPagination, kernel, type]);

  const items = useMemo(() => {
    return localOrder.map(id => registry[id]).filter(Boolean) as any[];
  }, [localOrder, registry]);

  const users = useMemo(() => {
    if (type !== 'User') return [] as LeasingUserDTO[];
    return items as LeasingUserDTO[];
  }, [type, items]);

  const organisations = useMemo(() => {
    if (type !== 'Organisation') return [] as LeasingOrganisationDTO[];
    return items as LeasingOrganisationDTO[];
  }, [type, items]);

  const processedMembers = useMemo(() => users.map((user) => ({
    ...user,
    computedDisplayName: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
    timeAgo: formatTimeAgo(user.lastActive),
  })), [users]);

  const selectedItems = selectedIds.map(id => registry[id]).filter(Boolean);

  // A one-pick list (members, organisations) is ChatGPT's dropdown under its button (owner,
  // 2026-09-28: 'just a simple dropdown, no need for it to be a modal'); units and other galleries
  // keep their wide sheet.
  const isCompact = type === 'User' || type === 'Organisation';
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!modalOpen || isCompact) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'auto'; };
  }, [modalOpen, isCompact]);

  useEffect(() => {
    if (!modalOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
      setIsConfirmingSelection(false);
    }
  }, [modalOpen]);

  const handleItemClick = useCallback((id: string) => {
    onSelect(id);
    if (!isMultiSelect) setOpen(false);
  }, [onSelect, isMultiSelect, setOpen]);

  const handleClose = useCallback(() => setOpen(false), [setOpen]);

  const handleTriggerOpen = useCallback(() => {
    if (disabled) return;
    setAnchorRect(anchorEl?.getBoundingClientRect() ?? null);
    setOpen(true);
  }, [disabled, setOpen, anchorEl]);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen, setOpen]);

  const handleClearOrganisation = useCallback(() => {
    if (onClearSelection) onClearSelection();
    else onSelect('');
    if (!isMultiSelect) setOpen(false);
  }, [onClearSelection, onSelect, isMultiSelect, setOpen]);

  const handleConfirmSelection = useCallback(async () => {
    if (isConfirmingSelection) return;
    if (!onConfirmSelection) {
      setOpen(false);
      return;
    }

    try {
      setIsConfirmingSelection(true);
      await onConfirmSelection();
      setOpen(false);
    } catch {
      // Keep modal open so the caller can correct and retry.
    } finally {
      setIsConfirmingSelection(false);
    }
  }, [isConfirmingSelection, onConfirmSelection, setOpen]);

  const displayTitle = title || (type === 'User'
    ? 'Select a member'
    : type === 'Organisation'
      ? 'Select an organisation'
      : `Select ${type.toLowerCase()}`);

  const searchPlaceholder = type === 'User'
    ? 'Search members'
    : type === 'Organisation'
      ? 'Search organisations'
      : `Search ${type.toLowerCase()}s`;

  const filteredPropertyId = type === 'Unit' ? (filters?.propertyId as string | undefined) : undefined;
  const filteredProperty = filteredPropertyId ? propertyRegistry[filteredPropertyId] : null;
  const sortedUnitItems = useMemo(() => {
    if (type !== 'Unit') return [] as any[];
    const statusRank = (status?: string | null) => {
      if (status === 'AVAILABLE') return 0;
      if (status === 'EXPIRING_SOON') return 1;
      if (status === 'UNDER_OFFER') return 2;
      if (status === 'LEASED') return 3;
      return 4;
    };
    const hasOccupantSignal = (unit: any): number => {
      const status = typeof unit?.status === 'string' ? unit.status : '';
      const hasTenantName = typeof unit?.tenantName === 'string' && unit.tenantName.trim().length > 0;
      const occupiedByStatus =
        status === 'LEASED' ||
        status === 'EXPIRING_SOON' ||
        status === 'UNDER_OFFER' ||
        status === 'REPLACEMENT_UNDER_OFFER';
      return hasTenantName || occupiedByStatus ? 1 : 0;
    };

    return items
      .map((unit, index) => ({ unit, index }))
      .sort((a, b) => {
        const tenantDiff = hasOccupantSignal(a.unit) - hasOccupantSignal(b.unit);
        if (tenantDiff !== 0) return tenantDiff;

        const rankDiff = statusRank(a.unit.status) - statusRank(b.unit.status);
        if (rankDiff !== 0) return rankDiff;
        return a.index - b.index;
      })
      .map(({ unit }) => unit);
  }, [type, items]);

  return (
    <div ref={setAnchorEl} className={className}>
      {renderTrigger(selectedItems, handleTriggerOpen)}

      {modalOpen && typeof document !== 'undefined' && createPortal((
        <div
          className={isCompact ? 'fixed inset-0 z-[200]' : 'fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-in fade-in duration-200'}
          onClick={handleClose}
        >
          <div
            role="dialog"
            aria-label={displayTitle}
            className={isCompact
              ? 'fixed flex max-h-[420px] w-[320px] flex-col overflow-hidden rounded-[20px] border border-white/[0.06] bg-[#353535] p-1.5 text-white shadow-[0_18px_52px_rgba(0,0,0,0.5)] animate-in fade-in zoom-in-95 duration-150'
              : 'relative flex max-h-[85vh] w-full max-w-6xl flex-col overflow-hidden rounded-[24px] border border-white/[0.06] bg-[#303030] text-white shadow-[0_24px_64px_rgba(0,0,0,0.6)]'}
            style={isCompact && anchorRect ? {
              top: anchorRect.bottom + 8,
              left: Math.max(8, Math.min(anchorRect.right - 320, window.innerWidth - 328)),
            } : undefined}
            onClick={stopPropagation}
          >
            {isCompact ? null : (
            <div className="flex items-center justify-between px-5 pb-3 pt-5">
              <h2 className="font-semibold text-[#ECECEC]" style={{ fontSize: 18, lineHeight: '26px' }}>{displayTitle}</h2>
              <button type="button" onClick={handleClose} aria-label="Close" className="-mr-1.5 flex h-9 w-9 items-center justify-center rounded-full text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white">
                <X size={18} />
              </button>
            </div>
            )}

            <div className={isCompact ? 'px-1 pb-1.5 pt-1' : 'px-5 pb-3'}>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#AFAFAF]" size={16} />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  value={search}
                  onChange={handleSearchChange}
                  className={clsx('w-full rounded-full border border-white/[0.08] bg-transparent pl-10 pr-4 text-[#ECECEC] outline-none transition-colors placeholder:text-[#AFAFAF] focus:border-[#555555]', isCompact ? 'h-9' : 'h-10')}
                  style={{ fontSize: 14 }}
                  autoFocus
                />
              </div>
            </div>

            <div
              className={clsx('custom-scrollbar flex-1 overflow-y-auto', isCompact ? '' : 'px-6 pb-6 pt-2')}
              onScroll={handleScroll}
            >
              {localIsLoading && items.length === 0 ? (
                <div className={clsx('flex justify-center', isCompact ? 'py-6' : 'py-20')}><SimpleLoader /></div>
              ) : type === 'User' ? (
                processedMembers.length === 0 ? (
                  <p className="px-3 py-8 text-center text-[#8E8E8E]" style={{ fontSize: 14 }}>No members found</p>
                ) : (
                  <div className="flex flex-col">
                    {processedMembers.map((member) => (
                      <PickerRow
                        key={member.id}
                        id={member.id}
                        name={member.computedDisplayName}
                        detail={`${member.email} · ${member.timeAgo}`}
                        visual={<UserAvatar userId={member.id} name={member.computedDisplayName} canEdit={false} isOrganisationAdmin={false} size={32} shape="circle" />}
                        isSelected={selectedIds.includes(member.id)}
                        onSelect={handleItemClick}
                      />
                    ))}
                    {localIsLoading && <div className="flex justify-center py-4"><SimpleLoader /></div>}
                  </div>
                )
              ) : type === 'Organisation' ? (
                <div className="flex flex-col">
                  {/* 'All' only where the caller can mean it (a filter), not where one must be chosen. */}
                  {onClearSelection ? (
                    <PickerRow
                      id=""
                      name="All organisations"
                      detail="Everything, across every organisation"
                      visual={<Globe size={18} />}
                      isSelected={selectedIds.length === 0}
                      onSelect={handleClearOrganisation}
                    />
                  ) : null}
                  {organisations.map((org) => (
                    <PickerRow
                      key={org.id}
                      id={org.id}
                      name={org.name}
                      visual={org.logoUrl
                        ? <img src={`/api/organisation/logo?orgId=${org.id}`} alt="" className="h-5 w-5 object-contain" />
                        : <Building size={18} />}
                      isSelected={selectedIds.includes(org.id)}
                      onSelect={handleItemClick}
                    />
                  ))}
                  {organisations.length === 0 && !localIsLoading && (
                    <p className="px-3 py-8 text-center text-[#8E8E8E]" style={{ fontSize: 14 }}>No organisations found</p>
                  )}
                  {localIsLoading && organisations.length > 0 && <div className="flex justify-center py-4"><SimpleLoader /></div>}
                </div>
              ) : type === 'Unit' ? (
                <div className="max-w-6xl mx-auto">
                  {filteredProperty && (
                    <div className="mb-6 px-4 py-2 rounded-xl border border-white/10 bg-white/[0.03] inline-flex items-center gap-2">
                      <Building size={14} className="text-white/50" />
                      <span className="text-xs text-white/80">
                        Filtered on property: <span className="font-semibold text-white">{filteredProperty.name}</span>
                      </span>
                    </div>
                  )}

                  {items.length === 0 && !localIsLoading ? (
                    <div className="text-center py-20 text-neutral-600 font-medium">
                      <p className="text-xl">No units found</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                      {sortedUnitItems.map((unit: any) => (
                        <UnitCard
                          key={unit.id}
                          unit={unit}
                          isSelected={selectedIds.includes(unit.id)}
                          hideProperty={Boolean(filteredProperty)}
                          onSelect={handleItemClick}
                        />
                      ))}
                    </div>
                  )}

                  {localIsLoading && items.length > 0 && (
                    <div className="py-8 flex justify-center"><SimpleLoader /></div>
                  )}
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-20 text-neutral-600 font-medium">
                  <p className="text-xl">No results found</p>
                </div>
              ) : (
                <div className="max-w-6xl mx-auto">
                  <div className={clsx(
                    variant === 'row'
                      ? 'flex flex-col gap-2'
                      : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12'
                  )}>
                    {items.map(item => (
                      <GenericEntityCard
                        key={item.id}
                        type={type}
                        item={item}
                        variant={variant}
                        isSelected={selectedIds.includes(item.id)}
                        onSelect={handleItemClick}
                      />
                    ))}
                  </div>

                  {localIsLoading && items.length > 0 && (
                    <div className="py-8 flex justify-center"><SimpleLoader /></div>
                  )}
                </div>
              )}
            </div>

            {isMultiSelect ? (
              <div className="flex items-start justify-between gap-4 border-t border-white/[0.06] px-5 py-4">
                <div className="min-w-0">
                  <span className="text-[#AFAFAF]" style={{ fontSize: 14 }}>{selectedIds.length} selected</span>
                  {multiSelectFooterExtra ? <div className="mt-3">{multiSelectFooterExtra}</div> : null}
                </div>
                <button
                  type="button"
                  onClick={handleConfirmSelection}
                  disabled={isConfirmingSelection}
                  className="inline-flex h-9 items-center rounded-full bg-[#F0F0F0] px-4 font-medium text-black transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                  style={{ fontSize: 14 }}
                >
                  {isConfirmingSelection ? 'Saving…' : 'Confirm'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ), document.body)}
    </div>
  );
};
